<template>
  <div class="satisfaction-metrics">
    <div class="chart-container">
      <h5><i class="fal fa-smile text-warning"></i> Métricas de Satisfacción del Usuario</h5>

      <!-- Puntuación General -->
      <div class="overall-satisfaction">
        <div class="row">
          <div class="col-md-6">
            <div class="satisfaction-score-card">
              <div class="score-circle" :class="getScoreClass(satisfaccionMetrics.general)">
                <div class="score-value">{{ satisfaccionMetrics.general || 0 }}</div>
                <div class="score-label">Puntuación General</div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="satisfaction-trends">
              <h6>Tendencias de Satisfacción</h6>
              <div class="trend-item" v-for="(valor, periodo) in satisfaccionMetrics.tendencias" :key="periodo">
                <div class="trend-period">{{ getPeriodLabel(periodo) }}</div>
                <div class="trend-value">{{ valor }}</div>
                <div class="trend-bar">
                  <div class="trend-progress" :style="{ width: (valor / 5) * 100 + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Satisfacción por Agente -->
      <div class="agent-satisfaction">
        <h6><i class="fal fa-users text-primary"></i> Satisfacción por Agente</h6>
        <div class="agent-satisfaction-grid">
          <div class="agent-satisfaction-item"
               v-for="(puntuacion, agente) in satisfaccionMetrics.por_agente"
               :key="agente">
            <div class="agent-avatar">
              <i class="fal fa-user"></i>
            </div>
            <div class="agent-info">
              <div class="agent-name">{{ agente }}</div>
              <div class="agent-score" :class="getScoreClass(puntuacion)">
                {{ puntuacion }}
              </div>
            </div>
            <div class="agent-trend">
              <div class="trend-indicator" :class="getTrendClass(puntuacion)">
                <i :class="getTrendIcon(puntuacion)"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Satisfacción por Tipo -->
      <div class="type-satisfaction">
        <h6><i class="fal fa-tags text-success"></i> Satisfacción por Tipo de Solicitud</h6>
        <div class="type-satisfaction-chart">
          <div class="type-item" v-for="(puntuacion, tipo) in satisfaccionMetrics.por_tipo" :key="tipo">
            <div class="type-header">
              <div class="type-name">{{ tipo }}</div>
              <div class="type-score" :class="getScoreClass(puntuacion)">
                {{ puntuacion }}
              </div>
            </div>
            <div class="type-bar">
              <div class="type-progress"
                   :class="getScoreClass(puntuacion)"
                   :style="{ width: (puntuacion / 5) * 100 + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Análisis de Correlación -->
      <div class="correlation-analysis">
        <div class="row">
          <div class="col-md-6">
            <div class="correlation-card">
              <div class="correlation-icon text-info">
                <i class="fal fa-chart-line"></i>
              </div>
              <div class="correlation-content">
                <div class="correlation-value">{{ (satisfaccionMetrics.correlacion_tiempo * 100).toFixed(0) }}%</div>
                <div class="correlation-label">Correlación con Tiempo de Resolución</div>
                <div class="correlation-description">
                  {{ getCorrelationDescription(satisfaccionMetrics.correlacion_tiempo) }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="feedback-summary">
              <h6>Resumen de Feedback</h6>
              <div class="feedback-stats">
                <div class="feedback-stat positive">
                  <div class="stat-icon">
                    <i class="fal fa-thumbs-up"></i>
                  </div>
                  <div class="stat-content">
                    <div class="stat-value">{{ satisfaccionMetrics.feedback_positivo }}%</div>
                    <div class="stat-label">Feedback Positivo</div>
                  </div>
                </div>
                <div class="feedback-stat negative">
                  <div class="stat-icon">
                    <i class="fal fa-thumbs-down"></i>
                  </div>
                  <div class="stat-content">
                    <div class="stat-value">{{ satisfaccionMetrics.feedback_negativo }}%</div>
                    <div class="stat-label">Feedback Negativo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SatisfactionMetrics',
  props: {
    satisfaccionMetrics: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    getScoreClass(puntuacion) {
      if (puntuacion >= 4.5) return 'score-excellent'
      if (puntuacion >= 4.0) return 'score-good'
      if (puntuacion >= 3.5) return 'score-average'
      return 'score-poor'
    },

    getPeriodLabel(periodo) {
      const labels = {
        'ultima_semana': 'Última Semana',
        'ultimo_mes': 'Último Mes',
        'ultimo_trimestre': 'Último Trimestre'
      }
      return labels[periodo] || periodo
    },

    getTrendClass(puntuacion) {
      if (puntuacion >= 4.5) return 'trend-up'
      if (puntuacion >= 4.0) return 'trend-stable'
      return 'trend-down'
    },

    getTrendIcon(puntuacion) {
      if (puntuacion >= 4.5) return 'fal fa-arrow-up'
      if (puntuacion >= 4.0) return 'fal fa-minus'
      return 'fal fa-arrow-down'
    },

    getCorrelationDescription(correlacion) {
      if (correlacion >= 0.7) return 'Fuerte correlación negativa: menor tiempo = mayor satisfacción'
      if (correlacion >= 0.5) return 'Correlación moderada: el tiempo afecta la satisfacción'
      if (correlacion >= 0.3) return 'Correlación débil: el tiempo tiene poco impacto'
      return 'Sin correlación significativa'
    }
  }
}
</script>

<style scoped>
.satisfaction-metrics {
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.chart-container h5 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.overall-satisfaction {
  margin-bottom: 30px;
}

.satisfaction-score-card {
  text-align: center;
  padding: 20px;
}

.score-circle {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: white;
  font-weight: bold;
}

.score-excellent { background: linear-gradient(135deg, #28a745 0%, #20c997 100%); }
.score-good { background: linear-gradient(135deg, #17a2b8 0%, #20c997 100%); }
.score-average { background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%); color: #212529; }
.score-poor { background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); }

.score-value {
  font-size: 2.5rem;
  margin-bottom: 5px;
}

.score-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

.satisfaction-trends {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.satisfaction-trends h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.trend-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 15px;
}

.trend-period {
  flex: 1;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.trend-value {
  font-weight: bold;
  color: #495057;
  min-width: 40px;
}

.trend-bar {
  flex: 2;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.trend-progress {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
}

.agent-satisfaction {
  margin-bottom: 30px;
}

.agent-satisfaction h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.agent-satisfaction-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.agent-satisfaction-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 15px;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1f4e79 0%, #2c5aa0 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.agent-info {
  flex-grow: 1;
}

.agent-name {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.agent-score {
  font-weight: bold;
  font-size: 1.1rem;
}

.trend-indicator {
  font-size: 1.2rem;
}

.trend-up { color: #28a745; }
.trend-stable { color: #17a2b8; }
.trend-down { color: #dc3545; }

.type-satisfaction {
  margin-bottom: 30px;
}

.type-satisfaction h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.type-satisfaction-chart {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.type-item {
  margin-bottom: 15px;
}

.type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.type-name {
  font-weight: 600;
  color: #495057;
}

.type-score {
  font-weight: bold;
  font-size: 1.1rem;
}

.type-bar {
  height: 12px;
  background-color: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.type-progress {
  height: 100%;
  transition: width 0.3s ease;
}

.type-progress.score-excellent { background: linear-gradient(90deg, #28a745, #20c997); }
.type-progress.score-good { background: linear-gradient(90deg, #17a2b8, #20c997); }
.type-progress.score-average { background: linear-gradient(90deg, #ffc107, #fd7e14); }
.type-progress.score-poor { background: linear-gradient(90deg, #dc3545, #c82333); }

.correlation-analysis {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.correlation-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 15px;
}

.correlation-icon {
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 2rem;
}

.correlation-content {
  flex-grow: 1;
}

.correlation-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 5px;
}

.correlation-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.correlation-description {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
}

.feedback-summary h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.feedback-stats {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.feedback-stat {
  display: flex;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  gap: 15px;
}

.feedback-stat.positive {
  background-color: #d4edda;
  border-left: 4px solid #28a745;
}

.feedback-stat.negative {
  background-color: #f8d7da;
  border-left: 4px solid #dc3545;
}

.stat-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.feedback-stat.positive .stat-icon {
  color: #28a745;
}

.feedback-stat.negative .stat-icon {
  color: #dc3545;
}

.stat-content {
  flex-grow: 1;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .score-circle {
    width: 120px;
    height: 120px;
  }

  .score-value {
    font-size: 2rem;
  }

  .score-label {
    font-size: 0.8rem;
  }

  .agent-satisfaction-grid {
    grid-template-columns: 1fr;
  }

  .correlation-value {
    font-size: 1.2rem;
  }

  .stat-value {
    font-size: 1rem;
  }
}
</style>
